import { Box, Typography, useMediaQuery, IconButton } from "@mui/material";
import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { logUserAction } from "../../utils/analytics";

export const CompactSearchProductCard = ({ product, hideBrand = false }) => {
  const navigate = useNavigate();
  const isSearchPage = window.location.pathname === "/search";
  const query = isSearchPage ? localStorage.getItem("query") : null;

  if (!product) return null;

  return (
    <Box
      onClick={() => {
        // Run logging asynchronously to avoid blocking navigation
        logUserAction({
          action: "product_click",
          data: {
            product_id: product.product_id || null,
            product_title: product.title || null,
            brand: product.brand || null,
            product_price:
              product?.price?.discounted ||
              product?.price?.original ||
              null,
            currency: product?.price?.currency || null,
            product_image: product?.image_url || null,
          },
          timestamp: new Date().toISOString(),
        });
        navigate("/product", {
          state: {
            product: product,
            similarProducts: [product],
            searchQuery: query,
          },
        });
      }}
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: "12px",
        overflow: "hidden",
        position: "relative",
        cursor: "pointer",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        backgroundColor: "#F9F9F9",
        willChange: { xs: "auto", md: "transform" },
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 24px rgba(0,0,0,0.1)",
        },
        "&:active": {
          transform: "translateY(-2px)",
        },
      }}
    >
      <img
        key={product?.image_url || 'no-image'}
        src={product?.image_url}
        alt={product.title}
        loading="lazy"
        onError={(e) => {
          const fallbackImage = product?.images?.[0];
          if (fallbackImage && !e.target.dataset.triedFallback) {
            e.target.dataset.triedFallback = "true";
            e.target.src = fallbackImage;
          } else {
            e.target.onerror = null;
            e.target.src = require("../../assets/images/no_image.png");
          }
        }}
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
      {/* Discount Badge */}
      {product.price?.discounted && product.price?.original && (
        <Box
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "#FF3B30",
            color: "#FFF",
            px: 1,
            py: 0.5,
            borderRadius: "4px",
            fontFamily: "Poppins-Bold",
            fontSize: "10px",
            letterSpacing: "0.5px"
          }}
        >
          SALE
        </Box>
      )}

      {/* Brand & Product Name Overlay */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)",
          px: 1.5,
          pt: 4,
          pb: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 0.25
        }}
      >
        {!hideBrand && (
          <Typography
            sx={{
              fontFamily: "Poppins-SemiBold",
              fontSize: { xs: "12px", sm: "14px" },
              color: "#FFF",
              letterSpacing: "0.2px",
              textTransform: "uppercase",
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          >
            {product.brand || product.showcaseBrand || "Brand"}
          </Typography>
        )}
        <Typography
          sx={{
            fontFamily: "Poppins-Regular",
            fontSize: { xs: "11px", sm: "13px" },
            color: "rgba(255,255,255,0.9)",
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
        >
          {product.title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.5 }}>
          {product.price?.discounted ? (
            <>
              <Typography
                sx={{
                  fontFamily: "Poppins-SemiBold",
                  fontSize: { xs: "12px", sm: "14px" },
                  color: "#FF5252",
                }}
              >
                {product.price.discounted} {product.price.currency}
              </Typography>
              {product.price.original && (
                <Typography
                  sx={{
                    fontFamily: "Poppins-Regular",
                    fontSize: { xs: "10px", sm: "12px" },
                    color: "rgba(255,255,255,0.6)",
                    textDecoration: "line-through",
                  }}
                >
                  {product.price.original} {product.price.currency}
                </Typography>
              )}
            </>
          ) : (
            <Typography
              sx={{
                fontFamily: "Poppins-SemiBold",
                fontSize: { xs: "12px", sm: "14px" },
                color: "#FFF",
              }}
            >
              {product.price?.original || "N/A"} {product.price?.currency || ""}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const SearchCard = ({ searchResults = [] }) => {
  const navigate = useNavigate();
  // Using md for the breakpoint so desktop gets the hero layout, mobile gets the Netflix swipe layout.
  const smallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const filteredResults = searchResults.filter((item) => {
    return (
      item?.products &&
      Array.isArray(item.products) &&
      item.products.length > 0
    );
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 4, md: 1.5 }, py: 1, px: { xs: 0, sm: 2 } }}>
      {filteredResults.map((brand, index) => {
        return (
          <BrandSection key={index} brand={brand} smallScreen={smallScreen} navigate={navigate} isLast={index === filteredResults.length - 1} />
        );
      })}
    </Box>
  );
};

const BrandSection = ({ brand, smallScreen, navigate, isLast }) => {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = React.useState(false);
  const [showRightArrow, setShowRightArrow] = React.useState(false);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollWidth, clientWidth } = scrollRef.current;
      const canScroll = scrollWidth > clientWidth + 10;
      setShowLeftArrow(canScroll);
      setShowRightArrow(canScroll);
    }
  };

  React.useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [brand]);

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -300, behavior: "smooth" });
    setTimeout(checkScroll, 350);
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 300, behavior: "smooth" });
    setTimeout(checkScroll, 350);
  };

  const handleViewAll = (e) => {
    e.stopPropagation();
    const slug = brand.name.replace(/\s+/g, '-');
    navigate(`/view-all/${encodeURIComponent(slug)}`);
  };

  const firstProduct = brand.products[0];
  const restProducts = brand.products.slice(1);

  const BrandHeader = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: smallScreen ? 1 : 2,
        px: { xs: 2, sm: 0 },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: "4px",
            height: "24px",
            borderRadius: "2px",
            background: "linear-gradient(180deg, #FF6B00 0%, #FFA785 100%)",
          }}
        />
        <Typography
          sx={{
            fontSize: { xs: "18px", sm: "24px" },
            fontFamily: "'Lora', serif",
            fontWeight: 600,
            color: "#1a1a1a",
            letterSpacing: "0.5px",
          }}
        >
          {brand.name}
        </Typography>
      </Box>
      <Typography
        onClick={handleViewAll}
        sx={{
          fontSize: { xs: "12px", sm: "14px" },
          fontFamily: "Poppins-Medium",
          color: "#5abccb",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          transition: "all 0.2s ease",
          "&:hover": {
            opacity: 0.8,
            textDecoration: "underline",
          },
        }}
      >
        View All <ArrowForwardIosIcon sx={{ fontSize: "10px" }} />
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", mb: 1, pb: smallScreen ? 0 : 2, borderBottom: (isLast || smallScreen) ? "none" : "1px solid rgba(0,0,0,0.06)" }}>
      {/* Show Header on top for mobile */}
      {smallScreen && BrandHeader}

      {/* Layout Selection */}
      {!smallScreen ? (
        // Desktop Layout (Plush style: Large Hero Card + Scrollable smaller cards)
        <Box sx={{ display: "flex", gap: 2, position: "relative" }}>
          {/* Hero First Product */}
          <Box
            sx={{
              width: "35%",
              height: "360px", // Fixed height for hero card
              flexShrink: 0,
            }}
          >
             <CompactSearchProductCard product={firstProduct} hideBrand={true} />
          </Box>
          
          {/* Remaining Products Horizontal Scroll */}
          <Box sx={{ width: "65%", display: "flex", flexDirection: "column" }}>
            {/* Show Header above scrollable cards for desktop */}
            {BrandHeader}

            <Box sx={{ flex: 1, display: "flex", alignItems: "center", position: "relative" }}>
              {restProducts.length > 0 ? (
                <>
                  <Box
                    ref={scrollRef}
                    sx={{
                      display: "flex",
                      gap: 2,
                      overflowX: "auto",
                      pb: 0.5,
                      "&::-webkit-scrollbar": { display: "none" },
                      scrollbarWidth: "none",
                      width: "100%",
                      transform: "translateZ(0)", // Force GPU acceleration
                    }}
                  >
                    {restProducts.map((prod, idx) => (
                      <Box key={idx} sx={{ minWidth: "180px", maxWidth: "180px", height: "260px", flexShrink: 0 }}>
                        <CompactSearchProductCard product={prod} hideBrand={true} />
                      </Box>
                    ))}
                  </Box>
                  {/* Navigation Arrows */}
                  {showLeftArrow && (
                    <IconButton
                      onClick={scrollLeft}
                      sx={{
                        position: "absolute",
                        left: -15,
                        top: "50%",
                        transform: "translateY(-50%)",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        zIndex: 2,
                        "&:hover": { backgroundColor: "#fff" },
                      }}
                    >
                      <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                  )}
                  {showRightArrow && (
                    <IconButton
                      onClick={scrollRight}
                      sx={{
                        position: "absolute",
                        right: -15,
                        top: "50%",
                        transform: "translateY(-50%)",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        zIndex: 2,
                        "&:hover": { backgroundColor: "#fff" },
                      }}
                    >
                      <ArrowForwardIosIcon fontSize="small" />
                    </IconButton>
                  )}
                </>
              ) : (
                 <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "260px", width: "100%" }}>
                   <Typography sx={{ color: "#666", fontFamily: "Poppins-Medium" }}>More products coming soon.</Typography>
                 </Box>
              )}
            </Box>
          </Box>
        </Box>
      ) : (
        // Mobile Layout (Netflix style: Scrollable cards, same size)
        <Box
          sx={{
            display: "flex",
            gap: 2,
            overflowX: "auto",
            px: 2,
            pb: 2,
            scrollSnapType: "x mandatory",
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          {brand.products.map((prod, idx) => (
            <Box key={idx} sx={{ minWidth: "150px", maxWidth: "150px", height: "220px", flexShrink: 0, scrollSnapAlign: "start" }}>
              <CompactSearchProductCard product={prod} hideBrand={true} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SearchCard;
